<template>
  <div class="ddp" ref="root">
    <button type="button" class="ddp-trigger" :class="{ 'ddp-trigger-compact': compact }" :style="width ? { width, minWidth: width, boxSizing: 'border-box' } : null" ref="trigger" @click="toggleOpen">
      <span>{{ displayValue }}</span>
      <span class="ddp-caret">&#9662;</span>
    </button>

    <div v-if="isOpen" class="ddp-panel" ref="panel" :style="panelStyle">
      <div class="ddp-panel-header">
        <button type="button" class="ddp-nav-button" @click="prevMonth" aria-label="Previous month">&#8249;</button>
        <span class="ddp-month-label">{{ monthLabel }}</span>
        <button type="button" class="ddp-nav-button" @click="nextMonth" aria-label="Next month">&#8250;</button>
      </div>

      <div class="ddp-weekdays">
        <span v-for="wd in weekdayLabels" :key="wd">{{ wd }}</span>
      </div>

      <div class="ddp-days">
        <button
          type="button"
          v-for="cell in calendarCells"
          :key="cell.dateString"
          class="ddp-day"
          :class="{ 'is-outside': !cell.isCurrentMonth, 'is-today': cell.isToday, 'is-selected': cell.dateString === pendingDate }"
          @click="selectDay(cell.dateString)"
        >{{ cell.dayNumber }}</button>
      </div>

      <div class="ddp-panel-footer">
        <button type="button" class="ddp-footer-button ddp-today-button" @click="goToday">Today</button>
        <button v-if="clearable" type="button" class="ddp-footer-button ddp-clear-button" @click="clearSelection">Clear</button>
        <button type="button" class="ddp-footer-button ddp-save-button" @click="applySelection">Save</button>
      </div>
    </div>
  </div>
</template>

<script>
const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTH_LABELS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// Local, calendar-only date helpers - deliberately avoid `new Date(dateOnlyString)`, which
// parses a bare "YYYY-MM-DD" as UTC midnight and then drifts a day in any timezone behind UTC
// when displayed (bit us in DuplicateGuestReview.vue's "last flown" dates - see that fix).
function parseDateOnly(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
  if (!match) return null;
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
}

function toDateString(year, month, day) {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function todayDateString() {
  const now = new Date();
  return toDateString(now.getFullYear(), now.getMonth() + 1, now.getDate());
}

function daysInMonth(year, month) {
  return new Date(year, month, 0).getDate(); // month is 1-indexed here, so this rolls back correctly
}

export default {
  name: 'DatePicker',
  props: {
    value: {
      type: String,
      default: '', // 'YYYY-MM-DD', or '' for no date selected
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: 'Select a date',
    },
    compact: {
      type: Boolean,
      default: false,
    },
    width: {
      type: String,
      default: null, // e.g. '260px' - overrides the default min-width when a consumer needs
      // this picker to match the exact width of sibling controls (a select, a button).
    },
  },
  data() {
    const parsed = parseDateOnly(this.value) || parseDateOnly(todayDateString());
    return {
      isOpen: false,
      viewYear: parsed.year,
      viewMonth: parsed.month, // 1-indexed
      pendingDate: this.value,
      panelStyle: {},
    };
  },
  computed: {
    weekdayLabels() {
      return WEEKDAY_LABELS;
    },
    monthLabel() {
      return `${MONTH_LABELS[this.viewMonth - 1]} ${this.viewYear}`;
    },
    displayValue() {
      const parsed = parseDateOnly(this.value);
      if (!parsed) return this.placeholder;
      if (this.compact) {
        return `${parsed.month}/${parsed.day}/${parsed.year}`;
      }
      return `${MONTH_LABELS[parsed.month - 1]} ${parsed.day}, ${parsed.year}`;
    },
    calendarCells() {
      const firstOfMonth = new Date(this.viewYear, this.viewMonth - 1, 1);
      const leadingBlanks = firstOfMonth.getDay(); // 0 (Sun) - 6 (Sat)
      const totalDaysThisMonth = daysInMonth(this.viewYear, this.viewMonth);

      const prevMonthYear = this.viewMonth === 1 ? this.viewYear - 1 : this.viewYear;
      const prevMonth = this.viewMonth === 1 ? 12 : this.viewMonth - 1;
      const totalDaysPrevMonth = daysInMonth(prevMonthYear, prevMonth);

      const nextMonthYear = this.viewMonth === 12 ? this.viewYear + 1 : this.viewYear;
      const nextMonth = this.viewMonth === 12 ? 1 : this.viewMonth + 1;

      const today = todayDateString();
      const cells = [];

      for (let i = leadingBlanks - 1; i >= 0; i--) {
        const day = totalDaysPrevMonth - i;
        const dateString = toDateString(prevMonthYear, prevMonth, day);
        cells.push({ dateString, dayNumber: day, isCurrentMonth: false, isToday: dateString === today });
      }
      for (let day = 1; day <= totalDaysThisMonth; day++) {
        const dateString = toDateString(this.viewYear, this.viewMonth, day);
        cells.push({ dateString, dayNumber: day, isCurrentMonth: true, isToday: dateString === today });
      }
      let nextDay = 1;
      while (cells.length % 7 !== 0) {
        const dateString = toDateString(nextMonthYear, nextMonth, nextDay);
        cells.push({ dateString, dayNumber: nextDay, isCurrentMonth: false, isToday: dateString === today });
        nextDay++;
      }
      return cells;
    },
  },
  watch: {
    value(newValue) {
      // Stay in sync if the parent changes the date some other way (e.g. session restore).
      if (!this.isOpen) {
        this.pendingDate = newValue;
        const parsed = parseDateOnly(newValue);
        if (parsed) {
          this.viewYear = parsed.year;
          this.viewMonth = parsed.month;
        }
      }
    },
  },
  beforeDestroy() {
    this.teardownOpenPanel();
  },
  methods: {
    toggleOpen() {
      if (this.isOpen) {
        this.closeWithoutApplying();
        return;
      }
      // Re-sync the panel to the last confirmed date every time it opens, so a prior
      // in-progress pick that was never saved doesn't linger.
      this.pendingDate = this.value;
      const parsed = parseDateOnly(this.value) || parseDateOnly(todayDateString());
      this.viewYear = parsed.year;
      this.viewMonth = parsed.month;
      this.isOpen = true;
      this.$nextTick(() => {
        // The dashboard's sticky header has its own z-index'd stacking context, which caps
        // every descendant (including this panel) below anything outside that context with
        // a competing z-index elsewhere on the page (a trip card's copy button, in practice -
        // no z-index on the panel could ever win that fight). Move the panel to a direct
        // child of <body> and position it with fixed coordinates so it always paints above
        // everything, regardless of which container it's triggered from.
        if (this.$refs.panel && this.$refs.panel.parentNode !== document.body) {
          document.body.appendChild(this.$refs.panel);
        }
        this.positionPanel();
        document.addEventListener('click', this.handleDocumentClick, true);
        window.addEventListener('resize', this.positionPanel);
        window.addEventListener('scroll', this.positionPanel, true);
      });
    },
    positionPanel() {
      if (!this.isOpen || !this.$refs.trigger) return;
      const rect = this.$refs.trigger.getBoundingClientRect();
      this.panelStyle = {
        position: 'fixed',
        top: `${rect.bottom + 4}px`,
        right: `${window.innerWidth - rect.right}px`,
      };
    },
    handleDocumentClick(event) {
      const clickedTrigger = this.$refs.root && this.$refs.root.contains(event.target);
      const clickedPanel = this.$refs.panel && this.$refs.panel.contains(event.target);
      if (!clickedTrigger && !clickedPanel) {
        this.closeWithoutApplying();
      }
    },
    teardownOpenPanel() {
      document.removeEventListener('click', this.handleDocumentClick, true);
      window.removeEventListener('resize', this.positionPanel);
      window.removeEventListener('scroll', this.positionPanel, true);
      if (this.$refs.panel && this.$refs.panel.parentNode === document.body) {
        document.body.removeChild(this.$refs.panel);
      }
    },
    closeWithoutApplying() {
      this.isOpen = false;
      this.teardownOpenPanel();
    },
    prevMonth() {
      if (this.viewMonth === 1) {
        this.viewMonth = 12;
        this.viewYear -= 1;
      } else {
        this.viewMonth -= 1;
      }
    },
    nextMonth() {
      if (this.viewMonth === 12) {
        this.viewMonth = 1;
        this.viewYear += 1;
      } else {
        this.viewMonth += 1;
      }
    },
    selectDay(dateString) {
      this.pendingDate = dateString;
    },
    goToday() {
      const today = todayDateString();
      const parsed = parseDateOnly(today);
      this.viewYear = parsed.year;
      this.viewMonth = parsed.month;
      this.pendingDate = today;
      this.applySelection();
    },
    clearSelection() {
      this.isOpen = false;
      this.teardownOpenPanel();
      this.pendingDate = '';
      if (this.value) {
        this.$emit('input', '');
        this.$emit('change', '');
      }
    },
    applySelection() {
      this.isOpen = false;
      this.teardownOpenPanel();
      if (this.pendingDate && this.pendingDate !== this.value) {
        this.$emit('input', this.pendingDate);
        this.$emit('change', this.pendingDate);
      }
    },
  },
};
</script>

<style scoped>
.ddp {
  position: relative;
  display: inline-block;
}

.ddp-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 200px;
  padding: 10px 12px;
  border: 1px solid #ccc;
  border-radius: 5px;
  background-color: #fff;
  cursor: pointer;
  font-size: 1rem;
}

.ddp-trigger:hover {
  border-color: var(--primary-eph-color, #007bff);
}

.ddp-trigger-compact {
  min-width: 110px;
  padding: 4px 8px;
  font-size: 0.85rem;
  margin-left: 0;
}

.ddp-caret {
  margin-left: auto;
  color: #888;
  font-size: 0.8em;
}

.ddp-panel {
  /* position/top/right are set inline via panelStyle - this panel is portaled to <body>
     at open time, so it's not laid out relative to .ddp here at all (see toggleOpen). */
  z-index: 2000;
  width: 280px;
  background-color: #fff;
  border: 1px solid #ccc;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  padding: 12px;
}

.ddp-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.ddp-month-label {
  font-weight: 600;
}

.ddp-nav-button {
  background: none;
  border: none;
  font-size: 1.3rem;
  line-height: 1;
  cursor: pointer;
  padding: 4px 10px;
  border-radius: 4px;
  color: #333;
}

.ddp-nav-button:hover {
  background-color: #f0f0f0;
}

.ddp-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
  font-size: 0.8rem;
  color: #888;
  margin-bottom: 4px;
}

.ddp-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}

.ddp-day {
  border: none;
  background: none;
  padding: 8px 0;
  border-radius: 50%;
  cursor: pointer;
  font-size: 0.9rem;
}

.ddp-day:hover {
  background-color: #f0f0f0;
}

.ddp-day.is-outside {
  color: #bbb;
}

.ddp-day.is-today {
  font-weight: 700;
  color: var(--primary-eph-color, #007bff);
}

.ddp-day.is-selected {
  background-color: var(--primary-eph-color, #007bff);
  color: #fff;
}

.ddp-panel-footer {
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #eee;
}

.ddp-footer-button {
  padding: 6px 14px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.9rem;
  border: 1px solid #ccc;
  background-color: #f8f9fa;
}

.ddp-today-button:hover,
.ddp-clear-button:hover {
  background-color: #e9ecef;
}

.ddp-save-button {
  background-color: var(--primary-eph-color, #007bff);
  color: #fff;
  border-color: var(--primary-eph-color, #007bff);
}

.ddp-save-button:hover {
  opacity: 0.9;
}
</style>
